import React, { useEffect, useRef, useState, Fragment } from 'react';
import {
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Tooltip,
    TextField,
    IconButton,
    FormControl,
    Select,
    MenuItem,
    ListItemText
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ClientPermissionsAccordion from './ClientPermissionsAccordion';
import AdminPanelSettingsSharpIcon from '@mui/icons-material/AdminPanelSettingsSharp';
import { clientIdsWithExternalPermission, getExternalClientPermissions } from '../../helpers/externalClientPermissions';
import { getRolesApi } from '../../services/projects';

const getRoleKey = (role: any) => {
    const roleName = role?.name ?? role?.Name ?? '';
    return roleName.charAt(0).toLowerCase() + roleName.slice(1);
};

const getPermissions = (roles: any[]) => roles.map((role: any) => ({
    roleID: role?.id ?? role?.roleID ?? role?.RoleID,
    key: getRoleKey(role),
    label: role?.name ?? role?.Name ?? ''
}));

const isAdminPermission = (permission: any) => permission?.label?.toLowerCase() === 'admin';

const getRoleMappings = (row: any) =>
    row?.customerClientRoleMappings || row?.clientUserMaps?.[0]?.customerClientRoleMappings || [];

const getSelectedRoleIds = (row: any) =>
    new Set(getRoleMappings(row).map((role: any) => role?.roleID ?? role?.RoleID));

const buildRoleMappings = (row: any, permissions: any[]) =>
    permissions
        .filter((perm) => row?.[perm.key])
        .map((perm) => ({ roleID: perm.roleID }));

const addRoleStateToClient = (client: any, permissions: any[]) => {
    const selectedRoleIds = getSelectedRoleIds(client);
    return {
        ...client,
        selected: client.id !== 0,
        ...permissions.reduce((acc: any, perm) => ({
            ...acc,
            [perm.key]: selectedRoleIds.has(perm.roleID) || !!client?.[perm.key]
        }), {})
    };
};

const buildClientRoleMappings = (client: any, permissions: any[]) =>
    client.selected ? buildRoleMappings(client, permissions) : [];

const withClientRoleMappings = (client: any, permissions: any[]) => ({
    ...client,
    customerClientRoleMappings: buildClientRoleMappings(client, permissions)
});

const getClientId = (client: any) => client?.clientID ?? client?.clientId ?? client?.id;

const getRoleSignature = (roleMappings: any[]) =>
    roleMappings
        .map((role: any) => role?.roleID ?? role?.RoleID)
        .filter((roleID: any) => roleID !== undefined && roleID !== null)
        .map(String)
        .sort()
        .join('|');

const getClientSignature = (clientUserMaps: any[] = [], permissions: any[]) => JSON.stringify(
    clientUserMaps
        .map((client: any) => ({
            clientID: String(getClientId(client)),
            selected: !!client.selected,
            roles: getRoleSignature(client.selected ? buildClientRoleMappings(client, permissions) : [])
        }))
        .sort((left, right) => left.clientID.localeCompare(right.clientID))
);

const hasClientChanges = (originalClientUserMaps: any[] = [], updatedClientUserMaps: any[] = [], permissions: any[]) =>
    getClientSignature(originalClientUserMaps, permissions) !== getClientSignature(updatedClientUserMaps, permissions);

const CustomTable = ({
    setPaginationModel,
    paginationModel,
    handleSave,
    labUserCheck,
    setIsLockStatus
}: any) => {
    const [tableData, setTableData] = useState<any>([]);
    const [errors, setErrors] = useState({});
    const [roles, setRoles] = useState<any[]>([]);
    const rolesRequestStarted = useRef(false);
    const permissions = getPermissions(roles);
    const [error, setError] = useState<any>({
        message: '',
        open: false
    });

    useEffect(() => {
        if (paginationModel?.data) {
            const updatedData = paginationModel?.data?.map(row => {
                const clientUserMaps = row?.clientUserMaps?.map((client: any) => addRoleStateToClient(client, permissions)) || [];
                return {
                    ...row,
                    clientUserMaps,
                    __originalClientUserMaps: clientUserMaps,
                    __originalCustomerClientRoleMappings: getRoleMappings(row),
                    ...permissions.reduce((acc: any, perm) => ({
                        ...acc,
                        [perm.key]: getSelectedRoleIds(row).has(perm.roleID) || !!row?.[perm.key] || clientUserMaps.some(client => client[perm.key])
                    }), {}),
                    hasAdmin: permissions.some((perm) =>
                        isAdminPermission(perm) && (getSelectedRoleIds(row).has(perm.roleID) || clientUserMaps.some(client => client[perm.key]) || !!row?.[perm.key])
                    )
                };
            });
            setTableData(updatedData);
        }
    }, [paginationModel?.data, roles])

    useEffect(() => {
        if (paginationModel?.resetPage) {
            setPaginationModel(old => ({ ...old, page: 0 }))
        }
    }, [paginationModel?.resetPage])

    useEffect(() => {
        if (rolesRequestStarted.current) return;

        rolesRequestStarted.current = true;
        getRoles();
    }, []);

    const getRoles = async () => {
        try {
            const response = await getRolesApi();
            if (response.status === 200) {
                setRoles(response?.data || []);
            } else {
                setError({ message: 'Failed to fetch roles', open: true });
            }
        } catch (error) {
            setError({ message: 'Failed to fetch roles', open: true });
        }
    };

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPaginationModel(old => ({ ...old, page: newPage }));
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setPaginationModel(old => ({ ...old, rowsPerPage: parseInt(event.target.value, 10) }))
    };

    const handleChange = (id: any, field: any, value: any) => {
        const updatedRows = tableData.map((row: any) => {
            if (row.id === id) {
                return { ...row, [field]: value };
            }
            return row;
        });

        setTableData(updatedRows);

        setErrors((prevErrors: any) => ({
            ...prevErrors,
            [id]: {
                ...prevErrors[id],
                [field]: ''
            },
        }));
    };

    const handlePermissionsChange = (id: any, selectedKeys: string[]) => {
        const updatedPermissions = permissions.reduce(
            (acc: any, perm) => ({ ...acc, [perm.key]: selectedKeys.includes(perm.key) }),
            {}
        );

        setTableData((prevData: any) =>
            prevData.map((row: any) =>
                row.id === id
                    ? (() => {
                        const nextRow = {
                            ...row,
                            ...updatedPermissions,
                            hasAdmin: permissions.some((perm) => isAdminPermission(perm) && updatedPermissions[perm.key]),
                        };
                        const customerClientRoleMappings = buildRoleMappings(nextRow, permissions);
                        const updatedClientUserMaps = row.clientUserMaps?.map((client: any) => withClientRoleMappings({ ...client, ...updatedPermissions }, permissions)) || [];
                        return {
                            ...nextRow,
                            customerClientRoleMappings,
                            areClientsModified: getRoleSignature(row.__originalCustomerClientRoleMappings || []) !== getRoleSignature(customerClientRoleMappings),
                            clientUserMaps: updatedClientUserMaps
                        };
                    })()
                    : row
            )
        );
    };

    const validateRow = (row: any) => {
        let isValid = true;
        const rowErrors: any = {};

        if (!row.firstName.trim()) {
            rowErrors.firstName = 'First name cannot be empty';
            isValid = false;
        }
        if (!row.lastName.trim()) {
            rowErrors.lastName = 'Last name cannot be empty';
            isValid = false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(row.email)) {
            rowErrors.email = 'Invalid email format';
            isValid = false;
        }

        if (!row.email.trim()) {
            rowErrors.email = 'Email cannot be empty';
            isValid = false;
        }

        setErrors((prevErrors: any) => ({
            ...prevErrors,
            [row.id]: rowErrors
        }));

        return isValid;
    }

    const handleClientChange = (id, updatedClientUserMaps, areClientsModified, hasAdmin) => {
        setTableData((prevData) =>
            prevData.map((row) => {
                if (row.id !== id) return row;
                return {
                    ...row,
                    clientUserMaps: [...updatedClientUserMaps],
                    areClientsModified: hasClientChanges(row.__originalClientUserMaps || [], updatedClientUserMaps, permissions),
                    hasAdmin: hasAdmin
                };
            })
        )
    }

    const getExternalClientId = (row: any) => {
        const primaryClient = row?.clientUserMaps?.[0];
        return primaryClient?.clientID || primaryClient?.id || row?.clientId || '';
    }

    const handleRowSave = (row: any) => {
        if (validateRow(row)) {
            const filterClientUserMaps = row.clientUserMaps.filter(client => client.selected) || [];
            const customerClientRoleMappings = buildRoleMappings(row, permissions);
            const clientUserMaps = filterClientUserMaps.length > 0
                ? filterClientUserMaps.map((client) => withClientRoleMappings(client, permissions))
                : [];
            const areClientsModified = hasClientChanges(row.__originalClientUserMaps || [], row.clientUserMaps || [], permissions);
            const { __originalClientUserMaps, __originalCustomerClientRoleMappings, customerClientRoleMappings: _customerClientRoleMappings, ...rowForSave } = row;
            const updatedRow = {
                ...rowForSave,
                clientUserMaps,
                areClientsModified,
                ...(externalUser ? {
                    clientId: getExternalClientId(row),
                    ...permissions.reduce((acc: any, perm) => ({ ...acc, [perm.key]: !!row[perm.key] }), {})
                } : {})
            }
            handleSave(updatedRow);
        }
    }

    const externalUser = labUserCheck?.UserType === "External";
    const managedClientIds = clientIdsWithExternalPermission(
        getExternalClientPermissions(labUserCheck?.ClientPermissions),
        ['ManageAllUsers', 'ManageUsers']
    );
    const canManageUser = (user: any) => {
        if (!externalUser) return true;

        const mappedClientIds = user?.clientUserMaps
            ?.map((client: any) => client?.clientID ?? client?.clientId ?? client?.id)
            .filter((clientId: any) => clientId !== undefined && clientId !== null) || [];
        const userClientIds = mappedClientIds.length > 0
            ? mappedClientIds
            : [user?.clientID ?? user?.clientId].filter((clientId) => clientId !== undefined && clientId !== null);

        return userClientIds.length > 0 && userClientIds.every((clientId: any) => managedClientIds.has(String(clientId)));
    };

    return (
        <>
            <TableContainer>
                <Table sx={{ minWidth: 700, border: '1px solid #ddd' }} aria-label="custom table" id="customTable">
                    <TableHead style={{ backgroundColor: 'rgb(0, 56, 131)' }}>
                        <TableRow style={{ height: '50px' }}>
                            <TableCell style={{ color: '#fff', padding: '0px 0px 0px 16px', width: '110px' }}>First Name</TableCell>
                            <TableCell style={{ color: '#fff', padding: '4px', width: '110px' }}>Last Name</TableCell>
                            <TableCell style={{ color: '#fff', padding: '4px', width: '220px' }}>Email</TableCell>
                            {labUserCheck && (labUserCheck?.UserType === 'Internal') ?
                                <>
                                    <TableCell style={{ color: '#fff', padding: '4px', width: '170px' }}>Client Name</TableCell>
                                </> : null}
                            {labUserCheck && (labUserCheck?.UserType !== 'Internal') ? <TableCell style={{ color: '#fff', padding: '4px', width: '220px' }}>Roles</TableCell> : null}
                            <TableCell style={{ color: '#fff', padding: '4px', width: '70px' }}>Is Active</TableCell>
                            <TableCell style={{ color: '#fff', padding: '4px', width: '70px' }}>Is Locked</TableCell>
                            <TableCell style={{ color: '#fff', padding: '4px', width: '50px' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableData && tableData.length > 0 ? tableData.map((user: any, index: any) => {
                            return (
                                <Fragment key={user.id}>
                                    <TableRow tabIndex={-1} key={user.index}>
                                        <TableCell style={{ padding: '4px' }}>
                                            <FormControl sx={{ paddingLeft: '10px', width: '90%' }}>
                                                <TextField
                                                    id="outlined-basic"
                                                    name="firstName"
                                                    label=""
                                                    sx={{
                                                        '.MuiOutlinedInput-input': {
                                                            padding: '8px 10px',
                                                            fontSize: '12px'
                                                        }
                                                    }}
                                                    variant="outlined"
                                                    size="small"
                                                    value={user.firstName}
                                                    onChange={(event: any) => handleChange(user.id, 'firstName', event.target.value)}
                                                    error={!!errors[user.id]?.firstName}
                                                    helperText={errors[user.id]?.firstName}
                                                />
                                            </FormControl>
                                        </TableCell>
                                        <TableCell style={{ padding: '4px' }}>
                                            <FormControl sx={{ width: '90%' }}>
                                                <TextField
                                                    id="outlined-basic"
                                                    name="lastName"
                                                    label=""
                                                    sx={{
                                                        '.MuiOutlinedInput-input': {
                                                            padding: '8px 10px',
                                                            fontSize: '12px'
                                                        }
                                                    }}
                                                    variant="outlined"
                                                    size="small"
                                                    value={user.lastName}
                                                    onChange={(event: any) => handleChange(user.id, 'lastName', event.target.value)}
                                                    error={!!errors[user.id]?.lastName}
                                                    helperText={errors[user.id]?.lastName}
                                                />
                                            </FormControl>
                                        </TableCell>
                                        <TableCell style={{ padding: '4px' }}>
                                            <FormControl sx={{ width: '90%' }}>
                                                <TextField
                                                    id="outlined-basic"
                                                    name="email"
                                                    label=""
                                                    sx={{
                                                        '.MuiOutlinedInput-input': {
                                                            padding: '8px 10px',
                                                            fontSize: '12px'
                                                        }
                                                    }}
                                                    variant="outlined"
                                                    size="small"
                                                    value={user.email}
                                                    onChange={(event: any) => handleChange(user.id, 'email', event.target.value)}
                                                    error={!!errors[user.id]?.email}
                                                    helperText={errors[user.id]?.email}
                                                />
                                            </FormControl>
                                        </TableCell>
                                        {labUserCheck && (labUserCheck?.UserType === 'Internal') ?
                                            <TableCell style={{ padding: '4px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <ClientPermissionsAccordion row={user} roles={roles} onChange={handleClientChange} />
                                                    {user.hasAdmin && <Tooltip title="Admin">
                                                        <AdminPanelSettingsSharpIcon className="admin-icon" color="primary" />
                                                    </Tooltip>}
                                                </div>
                                            </TableCell>
                                            : null}
                                        {labUserCheck && (labUserCheck?.UserType !== 'Internal') ?
                                            <TableCell style={{ padding: '4px' }}>
                                                <Tooltip title={!canManageUser(user) ? "Doesn't have necessary privileges!" : ""}>
                                                    <span style={{ display: 'block', width: '100%' }}>
                                                        <FormControl sx={{ width: '100%' }} size="small">
                                                            <Select
                                                                multiple
                                                                size="small"
                                                                displayEmpty
                                                                value={permissions.filter((perm) => user[perm.key]).map((perm) => perm.key)}
                                                                onChange={(event: any) => handlePermissionsChange(user.id, event.target.value)}
                                                                renderValue={(selected: any) =>
                                                                    selected.length === 0
                                                                        ? <span style={{ color: 'rgba(0, 0, 0, 0.5)' }}>Select Roles</span>
                                                                        : permissions.filter((perm) => selected.includes(perm.key)).map((perm) => perm.label).join(', ')
                                                                }
                                                                disabled={!canManageUser(user)}
                                                                MenuProps={{
                                                                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                                                                    PaperProps: { style: { maxHeight: '300px', overflowY: 'auto' } }
                                                                }}
                                                                sx={{
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    width: '100%',
                                                                    fontSize: '12px'
                                                                }}
                                                            >
                                                                {permissions.map((perm) => (
                                                                        <MenuItem key={perm.key} value={perm.key} style={{ fontSize: '12px' }} disabled={[2, 3].includes(perm.roleID)}>
                                                                            <Checkbox checked={!!user[perm.key]} disabled={[2, 3].includes(perm.roleID)} />
                                                                        <ListItemText primary={perm.label} />
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            : null}
                                        <TableCell style={{ padding: '4px' }}>
                                            <Tooltip title={!canManageUser(user) ? "Doesn't have necessary privileges!" : ""}>
                                                <span>
                                                    <Checkbox
                                                        name="isActive"
                                                        onChange={(event: any) => {
                                                            handleChange(user.id, event.target.name, event.target.checked);
                                                        }}
                                                        checked={user.isActive}
                                                        disabled={!canManageUser(user)}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell style={{ padding: '4px' }}>
                                            <Tooltip title={!canManageUser(user) ? "Doesn't have necessary privileges!" : ""}>
                                                <span>
                                                    <Checkbox
                                                        name="isLocked"
                                                        onChange={(event: any) => {
                                                            handleChange(user.id, event.target.name, event.target.checked);
                                                            setIsLockStatus(!event.target.checked);
                                                        }}
                                                        checked={user.isLocked}
                                                        disabled={!canManageUser(user)}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell style={{ padding: '4px' }}>
                                            <Tooltip title={!canManageUser(user) ? "Doesn't have necessary privileges!" : "Save"}>
                                                <span>
                                                    <IconButton color="primary" onClick={() => handleRowSave(user)} disabled={!canManageUser(user)}>
                                                        <SaveIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                </Fragment>
                            )
                        }) : (
                            <TableRow>
                                <TableCell
                                    colSpan={labUserCheck?.UserType === 'Internal' ? 7 : 7}
                                    align="center"
                                    style={{ padding: '24px', color: 'rgba(0, 0, 0, 0.6)' }}
                                >
                                    No rows
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10]}
                    component="div"
                    count={paginationModel?.total}
                    page={paginationModel?.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={paginationModel?.rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </>
    );
}

export default CustomTable;
